import { Fragment, useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { formatRelative } from '@/lib/time';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AGENT_TASK_STATES, agentTasksUrl, checksSummary, stateTone,
  type AgentTask, type AgentTasksPayload,
} from '@/lib/agent-tasks';

// Agent tab (ops-agent spec §4.5). Read-only: every traced console action
// and, later, every agent task, with its steps and definition-of-done checks.

const TONE_CLASS: Record<ReturnType<typeof stateTone>, string> = {
  ok: 'bg-emerald-500/10 text-emerald-700',
  bad: 'bg-red-500/10 text-red-700',
  warn: 'bg-amber-500/10 text-amber-700',
  neutral: 'bg-black/[0.05] text-subtle',
};

function StateBadge({ state }: { state: string }) {
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE_CLASS[stateTone(state)]}`}>{state}</span>;
}

function TaskDetail({ task }: { task: AgentTask }) {
  return (
    <div className="space-y-3 bg-black/[0.02] px-4 py-3 text-[12px]">
      {task.outcome?.failureReason && <p className="text-red-700">Failure: {task.outcome.failureReason}</p>}
      <div>
        <div className="mb-1 font-medium">Steps</div>
        {(task.steps || []).map((s, i) => (
          <div key={i} className="font-mono">
            {s.target} → {s.status} ({s.ms} ms) — {s.summary}
          </div>
        ))}
      </div>
      <div>
        <div className="mb-1 font-medium">Checks</div>
        {(task.checks || []).length === 0 && <div className="text-subtle">No checks for this action.</div>}
        {(task.checks || []).map((c) => (
          <div key={c.id}>
            <span className={c.passed ? 'text-emerald-700' : 'text-red-700'}>{c.passed ? '✓' : '✗'}</span> {c.id}
            {c.detail ? <span className="text-subtle"> — {c.detail}</span> : null}
          </div>
        ))}
      </div>
      <div className="text-subtle">
        Task <span className="font-mono">{task.taskId}</span>
        {task.entities?.submissionIds?.length ? ` · submission ${task.entities.submissionIds.join(', ')}` : ''}
        {task.entities?.storeIds?.length ? ` · store ${task.entities.storeIds.join(', ')}` : ''}
      </div>
    </div>
  );
}

export default function AgentTasks() {
  const [state, setState] = useState<string>('');
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, isLoading } = useSWR<AgentTasksPayload>(agentTasksUrl(state || undefined), get, { refreshInterval: 30_000 });

  return (
    <div className="space-y-4 px-7 py-6">
      <PageHeader
        title="Agent"
        subtitle="Every traced action: who did it, what ran, and whether its checks passed."
        actions={
          <select className="rounded border px-2 py-1 text-[13px]" value={state} onChange={(e) => setState(e.target.value)} aria-label="Filter by state">
            <option value="">All states</option>
            {AGENT_TASK_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        }
      />
      {error && <p className="text-[13px] text-red-700">Could not load tasks.</p>}
      {isLoading && <Skeleton className="h-40 w-full" />}
      {data && data.tasks.length === 0 && <p className="text-[13px] text-subtle">No tasks yet.</p>}
      {data && data.tasks.length > 0 && (
        <table className="w-full text-[13px]">
          <thead className="text-left text-[11px] uppercase text-subtle">
            <tr><th className="py-2">When</th><th>State</th><th>Category</th><th>Who</th><th>Checks</th></tr>
          </thead>
          <tbody>
            {data.tasks.map((t) => (
              <Fragment key={t.id}>
                <tr className="cursor-pointer border-t hover:bg-black/[0.02]" onClick={() => setOpen(open === t.id ? null : t.id)}>
                  <td className="py-2">{formatRelative(t.createdAt)}</td>
                  <td><StateBadge state={t.state} /></td>
                  <td className="font-mono text-[12px]">{t.category}</td>
                  <td>{t.actor || t.source?.kind}</td>
                  <td>{checksSummary(t.checks)}</td>
                </tr>
                {open === t.id && (
                  <tr><td colSpan={5}><TaskDetail task={t} /></td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
